import { Box } from "@eworkit/iris";
import { FormikHelpers, FormikProps } from "formik";
import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Text } from "@/components/Text";
import { clearVatNo, validateCreateCompany } from "@/data/companies";
import { useToggle } from "@/hooks/Toggle";
import { CreateCompanyFormik, ICreateCompanyFormValues } from "@/views/components/Company/Create";
import { useAskToJoinSubmit } from "@/views/components/Company/hooks";
import { useToasts } from "@/views/components/Toast/useToasts";

interface ICompanyStepProps {
  onClose: () => void;
  updateStep: Dispatch<SetStateAction<number>>;
  setCompanyId: Dispatch<SetStateAction<number>>;
  onConvertAccount: (values: ICreateCompanyFormValues) => Promise<void>;
}

const defaultFormValues: ICreateCompanyFormValues = {
  countryCode: null,
  location: null,
  name: "",
  vatNo: "",
  companyWebsite: "",
  size: null,
  orgNo: "",
};

const CompanySection = ({ onClose, onConvertAccount, updateStep, setCompanyId }: ICompanyStepProps) => {
  const history = useHistory();
  const { addToast } = useToasts();
  const { t } = useTranslation("session");

  const [showDetails, toggleShowDetails] = useToggle(false);
  const [showAskToJoin, toggleShowAskToJoin] = useToggle(false);

  const submitAskToJoin = useAskToJoinSubmit();

  const handleSubmit = async (values: ICreateCompanyFormValues, actions: FormikHelpers<ICreateCompanyFormValues>) => {
    if (showDetails) {
      await onConvertAccount(values);
      history.push("/");

      onClose();
      return;
    }

    const vatNumber = await validateCreateCompany({
      countryCode: values.countryCode!,
      vatNo: clearVatNo(values.countryCode!, values.vatNo),
      orgNo: values.orgNo,
    });

    if (vatNumber.exists) {
      toggleShowAskToJoin();
    } else if (!vatNumber.validVatNo) {
      actions.setFieldError("vatNo", "INVALID_VAT");
    } else {
      toggleShowDetails();
    }
  };

  const onAskToJoinSent = () => {
    updateStep(prev => prev + 1);

    addToast({
      text: t("ONBOARDING.PROFILE_TYPE_SELECT.SUPPLIER.REQUEST_TO_JOIN_SENT"),
      type: "success",
    });
  };

  const handleAskToJoin = async (values: ICreateCompanyFormValues, form: FormikProps<ICreateCompanyFormValues>) => {
    const { companyId, status: askToJoinResult } = await submitAskToJoin(values, form);

    if (askToJoinResult === "success") {
      setCompanyId(companyId);

      onAskToJoinSent();
    }
  };

  return (
    <Box mt="md">
      <Box mb="sm">
        <Text>{t("USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.COMPANY_SECTION.DESCRIPTION")}</Text>
      </Box>

      <CreateCompanyFormik
        onCancel={onClose}
        onSubmit={handleSubmit}
        profileInCompany={false}
        askToJoin={showAskToJoin}
        showDetails={showDetails}
        onAskToJoin={handleAskToJoin}
        initialValues={defaultFormValues}
      />
    </Box>
  );
};

export default CompanySection;
